// Spinner behavior:
// spinnerShow(spinner) / spinnerClose(spinner): show/hide a <spinner> element on demand from any
//   script that already has a reference to it - e.g. report-download.ts calls these directly
//   around its fetch() call, since it needs to hide the spinner at a specific point in its own
//   async flow rather than on a generic DOM event.
//
// Declarative usage via data attributes, for spinners that don't need any custom script of their
// own - just wire them up with attributes and this file handles showing them:
// data-wf-spinner-target: the ID of the element that triggers the spinner
// data-wf-spinner-event: a standard DOM event name (e.g. "submit", "change", "shown.bs.modal")
//   NOTE: this must be a real JS DOM event. For forms, use form.requestSubmit() rather than
//   form.submit() — form.submit() bypasses all event handlers (including this spinner, and
//   client-side validation). form.requestSubmit() fires the submit event, runs validation,
//   and respects any listeners before submitting.
// data-wf-spinner-display: the CSS display value to show the spinner with (default: "block")
// Once shown declaratively, the spinner stays visible until the page reloads or spinnerClose(...)
// is called on it explicitly.
// data-wf-spinner-disable-on-spin: opt-in (any non-empty value). When the target is a <form>, its
//   submit button(s) are disabled once the spinner shows, as double-submit prevention, and
//   re-enabled again when this same spinner is closed (via spinnerClose(...), or the page reloads
//   and none of this JS state exists anymore anyway). Opt-in rather than automatic: a form that
//   submits, updates itself via the response/AJAX, and expects to be resubmitted afterward
//   (without a full page reload in between) would otherwise get stuck with a permanently-disabled
//   button after the first submission.

// Buttons this module has disabled via disable-on-spin, keyed by the spinner that disabled them,
// so spinnerClose(...) knows what (if anything) to re-enable for that specific spinner.
const disabledButtonsBySpinner = new WeakMap<HTMLElement, (HTMLButtonElement | HTMLInputElement)[]>();

export function spinnerShow(spinner: HTMLElement, displayValue = 'block') {
    spinner.style.display = displayValue;
}

export function spinnerClose(spinner: HTMLElement) {
    spinner.style.display = 'none';

    const disabledButtons = disabledButtonsBySpinner.get(spinner);
    if (disabledButtons) {
        disabledButtons.forEach(button => button.disabled = false);
        disabledButtonsBySpinner.delete(spinner);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const spinners = document.querySelectorAll<HTMLElement>('[data-wf-spinner-target]');
    spinners.forEach(spinner => {
        const displayValue = spinner.getAttribute('data-wf-spinner-display') || 'block';
        const targetId = spinner.getAttribute('data-wf-spinner-target');
        const eventName = spinner.getAttribute('data-wf-spinner-event');
        const disableOnSpin = !!spinner.getAttribute('data-wf-spinner-disable-on-spin');

        if (!targetId || !eventName) return;

        const target = document.getElementById(targetId);
        if (!target) return;

        target.addEventListener(eventName, () => {
            spinnerShow(spinner, displayValue);

            if (disableOnSpin && target instanceof HTMLFormElement) {
                const buttons = Array.from(
                    target.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button[type="submit"], input[type="submit"]')
                );
                buttons.forEach(button => button.disabled = true);
                disabledButtonsBySpinner.set(spinner, buttons);
            }
        });
    });
});
